import {
  BarcodeOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  EditOutlined,
  LoadingOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Image,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Progress,
  Radio,
  Select,
  Space,
  Spin,
  Table,
  Tag as AntdTag,
  Tooltip,
  Typography,
} from "antd";
import type { InputRef } from "antd";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CASH_CHECK_MAX_WEIGHT_GRAMS,
  CASH_CHECK_MIN_WEIGHT_GRAMS,
  CashCheckErrorKey,
  CashCheckManualReason,
  CashCheckVerificationMethod,
  ICashCheckAmbiguousContext,
  ICashCheckUnknownBarcodeContext,
  ICashCheckLine,
  ICashCheckMutationResponse,
  ICashCheckScanRequest,
  ICashCheckWeightAdjustment,
  IOrderCashCheck,
  getCashCheckManualReasonName,
  getCashCheckVerificationMethodColor,
  getCashCheckVerificationMethodName,
  getCashCheckVerificationModeColor,
  getCashCheckVerificationModeName,
  isPendingAutomaticScaleScan,
  isWeightVerificationMethod,
  isWeightVerificationMode,
} from "../../../models/order/cash-check";
import { IProductsOrder } from "../../../models/order/products-order/response";
import EndPoints from "../../../services/end-points";
import { Permissions } from "../../../utils/Permissions/Permissions";
import {
  isScaleLabelCandidate,
  parseScalePackageBarcode,
} from "../../../utils/helpers/barcode";
import {
  filterEligibleCashCheckCandidateIds,
  resolveCashCheckScanCandidates,
} from "../../../utils/helpers/cash-check-scan-candidates";
import { errorNotification } from "../../../utils/helpers/notification";
import { formatPersonName } from "../../../utils/helpers/person";
import {
  formatRecordReservedQuantityWithUnit,
  toFiniteNumber,
} from "../../../utils/helpers/quantity";
import {
  formatSignedWeightFromGrams,
  formatWeightFromGrams,
  isWholeGramAmountInRange,
  kilogramsToGrams,
} from "../../../utils/helpers/weight";
import {
  analyzeWeightVerificationTimeline,
  isScaleScanVerification,
} from "../../../utils/helpers/weight-finalization";
import { normalizeSavedScalePackage } from "../../../utils/helpers/weight-packages";
import Tag from "../../general/antd/tag";
import styles from "./style.module.scss";

const { Panel } = Collapse;

export interface ICashCheckGate {
  enabled: boolean;
  isComplete: boolean;
  isBusy: boolean;
  syncFailed: boolean;
  isBlocked: boolean;
}

interface IProps {
  orderId: number;
  isCashStage: boolean;
  /** Explicit refresh requested by the parent after a server-side gate refusal. */
  refreshKey?: number;
  /** Order product lines from the order details response — the single source
   * for all display data (name, image, badges, reserved quantity) */
  orderProducts?: IProductsOrder[];
  /** Cash projection returned with the same OrderDetails snapshot. */
  initialCheck?: IOrderCashCheck;
  /** Finish-cash action shown in the panel header while in the Cash stage */
  canFinishCash?: boolean;
  finishing?: boolean;
  onFinishCash?: () => void;
  /** Re-fetch the whole order details — called on every conflict re-sync so
   * the joined display never diverges from the check state */
  onAuthoritativeRefresh?: () => Promise<IOrderCashCheck | null | undefined>;
  onGateChange?: (gate: ICashCheckGate | null) => void;
}

interface IPendingScan {
  scanToken: string;
  barcode: string;
  /** The sole locally eligible line, used only to keep pending feedback anchored. */
  matchedProductOrderId?: number;
  /** The line the operator explicitly picked after local or server ambiguity. */
  chosenProductOrderId?: number;
}

/** An ambiguous scan waiting for the operator to pick the intended line.
 * The barcode and token are replayed untouched — the first attempt recorded
 * nothing, so a new token would break idempotency. */
interface IAmbiguousScan {
  barcode: string;
  scanToken: string;
  candidateProductOrderIds: number[];
}

interface IManualWeightDraft {
  line: ICashCheckLine;
  operationToken: string;
}

interface IScanFeedback {
  type: "success" | "error" | "warning";
  text: string;
  /** The code the server read out of a scale label, rendered under the message
   * so the operator can compare it against the barcodes the order lines carry.
   * The bare code — the label around it belongs to the renderer. */
  parsedProductCode?: string;
}

const MIN_BARCODE_LENGTH = 3;
const SCAN_BUFFER_RESET_MS = 500;
const FEEDBACK_CLEAR_MS = 5000;

const getErrorKey = (error: any): string | undefined =>
  error?.response?.data?.errorKey;

const getErrorText = (error: any): string | undefined => {
  const errors = error?.response?.data?.errors;
  return Array.isArray(errors) && typeof errors[0] === "string"
    ? errors[0]
    : undefined;
};

const getErrorContext = (
  error: any,
): ICashCheckAmbiguousContext & ICashCheckUnknownBarcodeContext & any =>
  error?.response?.data?.context ?? {};

/** Refusals the operator can act on directly. The server text is always
 * preferred; the fallback only covers a missing translation upstream.
 * `cash_check_scale_line_ambiguous` and `cash_check_manual_weight_required`
 * are absent on purpose — they open a dialog instead of reporting. */
const CASH_CHECK_ERROR_FEEDBACK: Record<
  string,
  { type: IScanFeedback["type"]; fallbackKey: string }
> = {
  [CashCheckErrorKey.BARCODE_UNKNOWN]: {
    type: "error",
    fallbackKey: "cash_check_scan_rejected",
  },
  [CashCheckErrorKey.PRODUCT_NOT_IN_ORDER]: {
    type: "warning",
    fallbackKey: "cash_check_scan_rejected",
  },
  [CashCheckErrorKey.LINE_ALREADY_COMPLETE]: {
    type: "warning",
    fallbackKey: "cash_check_extra_piece",
  },
  [CashCheckErrorKey.SCAN_TOKEN_CONFLICT]: {
    type: "error",
    fallbackKey: "cash_check_scan_token_conflict_hint",
  },
  [CashCheckErrorKey.MUST_BE_ACCOUNTING_FINALIZATION]: {
    type: "error",
    fallbackKey: "cash_check_finish_blocked",
  },
  [CashCheckErrorKey.SCALE_BARCODE_INVALID]: {
    type: "error",
    fallbackKey: "cash_check_scale_barcode_invalid_hint",
  },
  [CashCheckErrorKey.SCALE_BARCODE_REQUIRED]: {
    type: "warning",
    fallbackKey: "cash_check_scale_barcode_required_hint",
  },
  [CashCheckErrorKey.SCALE_PRODUCT_NOT_WEIGHTED]: {
    type: "error",
    fallbackKey: "cash_check_scale_product_not_weighted_hint",
  },
  [CashCheckErrorKey.WEIGHT_PACKAGES_REQUIRED]: {
    type: "warning",
    fallbackKey: "cash_check_weight_packages_required_hint",
  },
  [CashCheckErrorKey.WEIGHT_PACKAGES_INVALID]: {
    type: "error",
    fallbackKey: "cash_check_weight_packages_invalid_hint",
  },
  [CashCheckErrorKey.WEIGHT_PACKAGE_WRONG_PRODUCT]: {
    type: "error",
    fallbackKey: "cash_check_weight_package_wrong_product_hint",
  },
  [CashCheckErrorKey.SCALE_UNDO_NOT_LATEST]: {
    type: "warning",
    fallbackKey: "cash_check_scale_undo_not_latest_hint",
  },
  [CashCheckErrorKey.SCALE_VERIFICATION_STALE]: {
    type: "warning",
    fallbackKey: "cash_check_scale_verification_stale_hint",
  },
  [CashCheckErrorKey.SCALE_UNDO_INVENTORY_UNAVAILABLE]: {
    type: "error",
    fallbackKey: "cash_check_scale_undo_inventory_unavailable_hint",
  },
  [CashCheckErrorKey.SCALE_FINANCIAL_SETTLEMENT_LOCKED]: {
    type: "error",
    fallbackKey: "cash_check_scale_financial_settlement_locked_hint",
  },
  [CashCheckErrorKey.INVENTORY_INVARIANT_FAILED]: {
    type: "error",
    fallbackKey: "cash_check_inventory_invariant_failed_hint",
  },
  [CashCheckErrorKey.WEIGHT_MODE_CONFLICT]: {
    type: "warning",
    fallbackKey: "cash_check_weight_mode_conflict_hint",
  },
  [CashCheckErrorKey.WEIGHT_FINALIZATION_CANNOT_BE_UNDONE]: {
    type: "error",
    fallbackKey: "cash_check_weight_finalization_cannot_be_undone_hint",
  },
};

const isAmbiguousRequestError = (error: any): boolean => {
  const status = error?.response?.status;
  return !status || status === 408 || (status >= 500 && status <= 599);
};

const createUuidV4 = (): string => {
  const cryptoApi = window.crypto;
  const cryptoWithRandomUuid = cryptoApi as Crypto & {
    randomUUID?: () => string;
  };

  if (typeof cryptoWithRandomUuid.randomUUID === "function") {
    return cryptoWithRandomUuid.randomUUID.call(cryptoApi);
  }

  const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
};

const OrderCashCheckPanel: React.FC<IProps> = ({
  orderId,
  isCashStage,
  refreshKey,
  orderProducts,
  initialCheck,
  canFinishCash,
  finishing,
  onFinishCash,
  onAuthoritativeRefresh,
  onGateChange,
}) => {
  const { t } = useTranslation();
  const userPermissions =
    localStorage.getItem("permisionStoroge")?.split(",") || [];
  const canScan = userPermissions.includes(
    Permissions.CAN_SCAN_ORDER_CASH_CHECK,
  );
  const canManual = userPermissions.includes(
    Permissions.CAN_MANUAL_ORDER_CASH_CHECK,
  );
  const canUndo = userPermissions.includes(
    Permissions.CAN_UNDO_ORDER_CASH_CHECK,
  );
  /** Gates every weight path: scale scans, manual weight and weight undo. */
  const canAdjustWeight = userPermissions.includes(
    Permissions.CAN_ADJUST_ORDER_WEIGHT_BY_SCALE,
  );

  const [check, setCheck] = useState<IOrderCashCheck | null>(
    initialCheck ?? null,
  );
  const [loading, setLoading] = useState(!initialCheck);
  const [pendingScan, setPendingScan] = useState<IPendingScan | null>(null);
  const [scanValue, setScanValue] = useState("");
  const [ambiguousScan, setAmbiguousScan] = useState<IAmbiguousScan | null>(
    null,
  );
  const [ambiguousChoice, setAmbiguousChoice] = useState<number>();
  const [mutatingIds, setMutatingIds] = useState<number[]>([]);
  const [manualLine, setManualLine] = useState<ICashCheckLine | null>(null);
  const [manualReason, setManualReason] = useState<CashCheckManualReason>();
  const [manualSaving, setManualSaving] = useState(false);
  const [weightDraft, setWeightDraft] = useState<IManualWeightDraft | null>(
    null,
  );
  const [weightKilograms, setWeightKilograms] = useState<number | null>(null);
  const [weightReason, setWeightReason] = useState<CashCheckManualReason>();
  const [weightSaving, setWeightSaving] = useState(false);
  const [weightResult, setWeightResult] =
    useState<ICashCheckWeightAdjustment | null>(null);
  const [expandedLineIds, setExpandedLineIds] = useState<React.Key[]>([]);
  const [feedback, setFeedback] = useState<IScanFeedback | null>(null);
  const [mutationBusy, setMutationBusy] = useState(false);
  const [syncFailed, setSyncFailed] = useState(false);

  const lastKeyAtRef = useRef(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const feedbackTimerRef = useRef<number>();
  const checkRequestIdRef = useRef(0);
  const activeMutationCountRef = useRef(0);
  const resyncRequestedRef = useRef(false);
  const resyncInFlightRef = useRef(false);
  const scanInFlightRef = useRef(false);
  const scannerInputRef = useRef<InputRef>(null);
  const submitScanRef = useRef<(barcode: string) => void>(() => undefined);

  const interactive = isCashStage && Boolean(check?.enabled);
  const scanActive = interactive && canScan;
  /** A scale label is indistinguishable from a product barcode client-side, so
   * a scan is only accepted once the previous one has been answered. */
  const scanLocked = Boolean(pendingScan) || Boolean(ambiguousScan);

  const weightUnitLabels = useMemo(
    () => ({
      gram: t("cash_check_unit_gram"),
      kilogram: t("cash_check_unit_kilogram"),
    }),
    [t],
  );

  // ---------- join with the order products (single display source) ----------
  const productOrderById = useMemo(() => {
    const map = new Map<number, IProductsOrder>();
    (orderProducts ?? []).forEach((productOrder) =>
      map.set(productOrder.id, productOrder),
    );
    return map;
  }, [orderProducts]);

  const getJoined = (line: ICashCheckLine) =>
    productOrderById.get(line.productOrderId);

  // the physically present product: the alternative when one was provided
  const getActualProduct = (line: ICashCheckLine): any => {
    const joined = getJoined(line);
    if (!joined) return undefined;
    return joined.isAlternative && joined.alternativeProduct
      ? joined.alternativeProduct
      : joined.product;
  };

  const getProductName = (line: ICashCheckLine) =>
    getActualProduct(line)?.name || `#${line.productOrderId}`;

  const getReservedQuantityDisplay = (line: ICashCheckLine) => {
    const joined = getJoined(line);
    return joined ? formatRecordReservedQuantityWithUnit(joined) : "-";
  };

  // ---------- sounds (Web Audio, no assets) ----------
  const playTones = useCallback(
    (
      tones: Array<{
        frequency: number;
        duration: number;
        type?: OscillatorType;
      }>,
    ) => {
      try {
        const AudioContextClass =
          window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContextClass) return;
        if (!audioContextRef.current) {
          audioContextRef.current = new AudioContextClass();
        }
        const context = audioContextRef.current;
        if (context.state === "suspended") context.resume();

        let startAt = context.currentTime;
        tones.forEach(({ frequency, duration, type = "sine" }) => {
          const oscillator = context.createOscillator();
          const gain = context.createGain();
          oscillator.type = type;
          oscillator.frequency.value = frequency;
          gain.gain.setValueAtTime(0.16, startAt);
          gain.gain.exponentialRampToValueAtTime(0.001, startAt + duration);
          oscillator.connect(gain);
          gain.connect(context.destination);
          oscillator.start(startAt);
          oscillator.stop(startAt + duration);
          startAt += duration + 0.04;
        });
      } catch {
        // sound is best-effort only
      }
    },
    [],
  );

  const playSuccess = useCallback(
    () =>
      playTones([
        { frequency: 990, duration: 0.08 },
        { frequency: 1320, duration: 0.1 },
      ]),
    [playTones],
  );
  const playError = useCallback(
    () =>
      playTones([
        { frequency: 220, duration: 0.16, type: "square" },
        { frequency: 180, duration: 0.24, type: "square" },
      ]),
    [playTones],
  );
  const playWarning = useCallback(
    () => playTones([{ frequency: 520, duration: 0.2, type: "triangle" }]),
    [playTones],
  );

  // ---------- state helpers ----------
  const showFeedback = useCallback((next: IScanFeedback) => {
    setFeedback(next);
    window.clearTimeout(feedbackTimerRef.current);
    feedbackTimerRef.current = window.setTimeout(
      () => setFeedback(null),
      FEEDBACK_CLEAR_MS,
    );
  }, []);

  /** The scanner input is the only capture path, so focus has to come back to
   * it after every dialog, refusal or completed operation. Focusing must not
   * move the page: the operator may be working on a row much farther down. */
  const focusScanner = useCallback(() => {
    window.setTimeout(() => {
      scannerInputRef.current?.input?.focus({ preventScroll: true });
    }, 0);
  }, []);

  const applyMutation = useCallback((mutation: ICashCheckMutationResponse) => {
    // The summary is always authoritative, even when the line is gone.
    if (mutation.shortageSummary) {
      setCheck((previous) =>
        previous
          ? { ...previous, shortageSummary: mutation.shortageSummary }
          : previous,
      );
    }

    // A historical verification can exceptionally outlive its ProductOrder.
    // line:null must never mutate local counters; the final authoritative
    // refresh below supplies the only safe replacement state.
    const mutationLine = mutation.line;
    if (!mutationLine) return;

    setCheck((previous) => {
      if (!previous) return previous;
      const exists = previous.lines.some(
        (line) => line.productOrderId === mutationLine.productOrderId,
      );
      const lines = exists
        ? previous.lines.map((line) =>
            line.productOrderId === mutationLine.productOrderId
              ? {
                  ...mutationLine,
                  barcodes: mutationLine.barcodes ?? line.barcodes,
                }
              : line,
          )
        : [
            ...previous.lines,
            {
              ...mutationLine,
              barcodes: mutationLine.barcodes ?? [],
            },
          ];

      return { ...previous, lines, isComplete: mutation.isCheckComplete };
    });
  }, []);

  const getCheck = useCallback(async (): Promise<IOrderCashCheck | null> => {
    const requestId = ++checkRequestIdRef.current;
    try {
      setLoading(true);
      const { data } = await EndPoints.order.getCashCheck(orderId);
      if (requestId === checkRequestIdRef.current) {
        setCheck(data);
        setSyncFailed(false);
        return data;
      }
      return null;
    } catch {
      // Keep existing content visible when a background refresh fails.
      if (requestId === checkRequestIdRef.current) {
        setSyncFailed(true);
      }
      return null;
    } finally {
      if (requestId === checkRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [orderId]);

  // Final batch re-sync. The details page supplies one atomic
  // GET /order/{id}?include=cashCheck snapshot so display data and counters
  // cannot come from different moments. The standalone GET remains a safe
  // fallback for isolated reuse of this panel.
  const resync = useCallback(async (): Promise<IOrderCashCheck | null> => {
    if (!onAuthoritativeRefresh) {
      const refreshedCheck = await getCheck();
      setSyncFailed(!refreshedCheck);
      return refreshedCheck;
    }

    const requestId = ++checkRequestIdRef.current;
    setLoading(true);
    try {
      const refreshedCheck = await onAuthoritativeRefresh();
      if (!refreshedCheck) {
        if (requestId === checkRequestIdRef.current) setSyncFailed(true);
        return null;
      }

      if (requestId === checkRequestIdRef.current) {
        setCheck(refreshedCheck);
        setSyncFailed(false);
        return refreshedCheck;
      }
      return null;
    } catch {
      if (requestId === checkRequestIdRef.current) setSyncFailed(true);
      return null;
    } finally {
      if (requestId === checkRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [getCheck, onAuthoritativeRefresh]);

  const flushMutationBatch = useCallback(async () => {
    if (
      resyncInFlightRef.current ||
      activeMutationCountRef.current > 0 ||
      !resyncRequestedRef.current
    ) {
      return;
    }

    resyncInFlightRef.current = true;
    try {
      while (
        activeMutationCountRef.current === 0 &&
        resyncRequestedRef.current
      ) {
        resyncRequestedRef.current = false;
        try {
          await resync();
        } catch {
          // The shared HTTP interceptor reports the failure. A later mutation
          // starts a fresh batch and requests another authoritative re-sync.
        }
      }
    } finally {
      resyncInFlightRef.current = false;
      if (activeMutationCountRef.current === 0 && !resyncRequestedRef.current) {
        setMutationBusy(false);
      }
    }
  }, [resync]);

  const beginMutation = useCallback(() => {
    activeMutationCountRef.current += 1;
    resyncRequestedRef.current = true;
    setMutationBusy(true);
  }, []);

  const finishMutation = useCallback(async (): Promise<void> => {
    activeMutationCountRef.current = Math.max(
      activeMutationCountRef.current - 1,
      0,
    );
    if (activeMutationCountRef.current === 0) {
      // Do not report the operation as settled while the parent order table,
      // totals and Cash projection still belong to the previous snapshot.
      // Concurrent mutations still collapse into the single authoritative
      // refresh managed by flushMutationBatch.
      await flushMutationBatch();
    }
  }, [flushMutationBatch]);

  useEffect(() => {
    if (initialCheck) {
      checkRequestIdRef.current += 1;
      setCheck(initialCheck);
      setLoading(false);
      setSyncFailed(false);
      return;
    }

    void resync();
  }, [initialCheck, isCashStage, refreshKey, resync]);

  const hasPendingWeightFinalization = Boolean(
    check?.lines.some((line) => line.hasPendingWeightFinalization),
  );

  // A SCALE_SCAN response describes the state at scan time. Once an
  // authoritative refresh confirms that no weighted line is pending (for
  // example after Finish in this or another tab), do not keep presenting that
  // historical response as if it still awaited finalization.
  useEffect(() => {
    if (isCashStage && hasPendingWeightFinalization) return;
    setWeightResult((previous) =>
      previous?.pendingFinalization ? null : previous,
    );
  }, [hasPendingWeightFinalization, isCashStage]);

  const totals = useMemo(() => {
    if (!check) return { required: 0, verified: 0 };
    return check.lines.reduce(
      (accumulator, line) => ({
        required: accumulator.required + line.requiredVerifications,
        verified:
          accumulator.verified +
          Math.min(line.verifiedCount, line.requiredVerifications),
      }),
      { required: 0, verified: 0 },
    );
  }, [check]);

  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    check?.lines.forEach((line) => {
      if (line.packageCount === null || line.packageCount === undefined) return;
      const scaleScanCount = line.verifications.filter(
        isScaleScanVerification,
      ).length;
      console.assert(
        line.packageCount === scaleScanCount,
        "Cash-check packageCount does not match active SCALE_SCAN records",
        {
          productOrderId: line.productOrderId,
          packageCount: line.packageCount,
          scaleScanCount,
        },
      );
    });
  }, [check]);

  const hasActiveVerifications = Boolean(
    check?.lines.some((line) => line.verifications.length > 0),
  );
  const isBusy = loading || mutationBusy;
  const finishBlocked =
    isBusy || syncFailed || (Boolean(check?.enabled) && !check?.isComplete);

  useEffect(() => {
    onGateChange?.(
      check
        ? {
            enabled: check.enabled,
            isComplete: check.isComplete,
            isBusy,
            isBlocked: finishBlocked,
            syncFailed,
          }
        : null,
    );
  }, [
    check?.enabled,
    check?.isComplete,
    finishBlocked,
    isBusy,
    onGateChange,
    syncFailed,
  ]);

  /**
   * The barcodes registered for the weighted lines. Shown beside the code the
   * scale actually sent, because neither side pads to a fixed length: a product
   * registered as `1234` will never match the `01234` a scale emits, and the
   * two lists side by side are the only way that reads as a data-entry mistake
   * rather than a broken scale.
   */
  const weightedLineBarcodes = useMemo(
    () =>
      Array.from(
        new Set(
          (check?.lines ?? [])
            .filter((line) => isWeightVerificationMode(line.verificationMode))
            .flatMap((line) => line.barcodes),
        ),
      ),
    [check],
  );

  const pendingLineId =
    pendingScan?.chosenProductOrderId ?? pendingScan?.matchedProductOrderId;
  // A scale label carries no product barcode we may read, so its scan stays
  // unattributed until the server answers.
  const hasUnattributedPending = Boolean(
    pendingScan && pendingLineId === undefined,
  );

  const findLine = useCallback(
    (productOrderId?: number) =>
      productOrderId === undefined
        ? undefined
        : check?.lines.find((line) => line.productOrderId === productOrderId),
    [check],
  );

  const openWeightModal = useCallback((line: ICashCheckLine) => {
    setWeightDraft({ line, operationToken: createUuidV4() });
    setWeightKilograms(null);
    setWeightReason(undefined);
  }, []);

  /** Single place where a refused cash-check operation becomes sound + text,
   * shared by scan, manual verify, manual weight and undo. */
  const reportOperationError = useCallback(
    (error: any) => {
      const errorKey = getErrorKey(error);
      const errorText = getErrorText(error);

      if (errorKey === CashCheckErrorKey.DISABLED) {
        setCheck((previous) =>
          previous ? { ...previous, enabled: false } : previous,
        );
        playWarning();
        showFeedback({
          type: "warning",
          text: errorText ?? t("cash_check_read_only_disabled"),
        });
        return;
      }

      const known = errorKey ? CASH_CHECK_ERROR_FEEDBACK[errorKey] : undefined;
      if (!known) {
        // Unmapped refusal or a plain validation error without `errorKey`.
        playError();
        errorNotification(errorText ?? t("operation_failed"));
        return;
      }

      if (known.type === "error") playError();
      else playWarning();

      // On an unknown scale label the server reports which product code it read
      // out of it. Showing that code is the only way the operator can tell a
      // mis-programmed scale from a genuinely absent product. Local parsing is
      // only a preview for candidate selection; the server remains authoritative.
      const parsedProductCode = getErrorContext(error).parsedProductCode;
      showFeedback({
        type: known.type,
        text: errorText ?? t(known.fallbackKey),
        parsedProductCode: parsedProductCode
          ? String(parsedProductCode)
          : undefined,
      });
    },
    [playError, playWarning, showFeedback, t],
  );

  // ---------- scan submission (single-flight, server-decided feedback) ----------
  /** Sends one scan attempt. `scan` carries the token so an ambiguous label can
   * be replayed on a chosen line without minting a new one. */
  const runScan = useCallback(
    async (scan: IPendingScan) => {
      if (scanInFlightRef.current) return;
      scanInFlightRef.current = true;

      beginMutation();
      setPendingScan(scan);
      setWeightResult(null);
      try {
        const request: ICashCheckScanRequest = {
          barcode: scan.barcode,
          scanToken: scan.scanToken,
        };
        // Only an operator's explicit choice, never the local match.
        if (scan.chosenProductOrderId !== undefined) {
          request.productOrderId = scan.chosenProductOrderId;
        }

        let mutation: ICashCheckMutationResponse;
        try {
          const { data } = await EndPoints.order.scanCashCheck(
            orderId,
            request,
            { suppressErrorNotification: true },
          );
          mutation = data;
        } catch (initialError: any) {
          if (!isAmbiguousRequestError(initialError)) throw initialError;

          // Network-level failure only: replay the identical token so the
          // server can recognize the retry instead of double-counting it.
          const { data } = await EndPoints.order.scanCashCheck(
            orderId,
            request,
          );
          mutation = data;
        }

        applyMutation(mutation);
        if (mutation.duplicate) {
          // Already recorded on an earlier attempt — no counter bump, no chime.
          showFeedback({
            type: "warning",
            text: t("cash_check_duplicate_operation"),
          });
        } else if (mutation.line) {
          playSuccess();
          if (mutation.weightAdjustment) {
            setWeightResult(mutation.weightAdjustment);
            const recordedWeight =
              mutation.weightAdjustment.packageMeasuredQuantityPhysical ??
              mutation.weightAdjustment.measuredQuantityPhysical;
            showFeedback({
              type: "success",
              text: `${t(
                mutation.weightAdjustment.pendingFinalization
                  ? "cash_check_weight_label_recorded"
                  : "cash_check_weight_recorded",
              )} — ${formatWeightFromGrams(recordedWeight, weightUnitLabels)}`,
            });
          } else {
            showFeedback({
              type: "success",
              text: `${t("cash_check_scan_accepted")} — ${scan.barcode}`,
            });
          }
        }
      } catch (error: any) {
        const errorKey = getErrorKey(error);

        if (errorKey === CashCheckErrorKey.SCALE_LINE_AMBIGUOUS) {
          const serverCandidates: number[] =
            getErrorContext(error).candidateProductOrderIds ?? [];
          const refreshedCheck = await resync();
          const candidates = filterEligibleCashCheckCandidateIds(
            refreshedCheck?.lines ?? check?.lines ?? [],
            serverCandidates,
          );

          if (candidates.eligibleProductOrderIds.length > 0) {
            playWarning();
            setAmbiguousScan({
              barcode: scan.barcode,
              scanToken: scan.scanToken,
              candidateProductOrderIds: candidates.eligibleProductOrderIds,
            });
            // Left unselected on purpose: the operator must pick the line that
            // was actually weighed, not confirm a default.
            setAmbiguousChoice(undefined);
          } else if (candidates.allMatchesAreCompletedNonWeighted) {
            playWarning();
            showFeedback({
              type: "warning",
              text: t("cash_check_all_matching_products_complete"),
            });
          } else {
            reportOperationError(error);
          }
        } else if (errorKey === CashCheckErrorKey.MANUAL_WEIGHT_REQUIRED) {
          const line = findLine(getErrorContext(error).productOrderId);
          playWarning();
          if (line && canManual && canAdjustWeight) {
            openWeightModal(line);
          } else {
            showFeedback({
              type: "warning",
              text:
                getErrorText(error) ??
                t("cash_check_manual_weight_required_hint"),
            });
          }
        } else {
          reportOperationError(error);
        }
      } finally {
        scanInFlightRef.current = false;
        setPendingScan(null);
        await finishMutation();
        focusScanner();
      }
    },
    [
      orderId,
      applyMutation,
      beginMutation,
      canAdjustWeight,
      canManual,
      check?.lines,
      findLine,
      finishMutation,
      focusScanner,
      openWeightModal,
      playSuccess,
      playWarning,
      reportOperationError,
      resync,
      showFeedback,
      t,
      weightUnitLabels,
    ],
  );

  const submitScan = useCallback(
    (barcode: string) => {
      if (!scanActive || scanInFlightRef.current) return;
      setScanValue("");

      // A scale prefix belongs to the device configuration, not the frontend.
      // Exactly 13 digits lets us produce a local product-code preview; the
      // untouched barcode still goes to the server for authoritative parsing.
      const parsed = isScaleLabelCandidate(barcode)
        ? parseScalePackageBarcode(barcode)
        : { shapeValid: false as const };
      const candidates = resolveCashCheckScanCandidates(
        check?.lines ?? [],
        barcode,
        parsed.shapeValid ? parsed.productCode : undefined,
      );
      const scanToken = createUuidV4();

      if (candidates.allMatchesAreCompletedNonWeighted) {
        playWarning();
        showFeedback({
          type: "warning",
          text: t("cash_check_all_matching_products_complete"),
        });
        focusScanner();
        return;
      }

      if (candidates.eligibleProductOrderIds.length > 1) {
        playWarning();
        setAmbiguousScan({
          barcode,
          scanToken,
          candidateProductOrderIds: candidates.eligibleProductOrderIds,
        });
        setAmbiguousChoice(undefined);
        return;
      }

      void runScan({
        scanToken,
        barcode,
        matchedProductOrderId: candidates.eligibleProductOrderIds[0],
      });
    },
    [
      check?.lines,
      focusScanner,
      playWarning,
      runScan,
      scanActive,
      showFeedback,
      t,
    ],
  );

  submitScanRef.current = submitScan;

  // The first attempt recorded nothing, so the replay keeps the same token.
  const confirmAmbiguousLine = useCallback(() => {
    if (!ambiguousScan || ambiguousChoice === undefined) return;
    const replay: IPendingScan = {
      barcode: ambiguousScan.barcode,
      scanToken: ambiguousScan.scanToken,
      chosenProductOrderId: ambiguousChoice,
    };
    setAmbiguousScan(null);
    setAmbiguousChoice(undefined);
    void runScan(replay);
  }, [ambiguousChoice, ambiguousScan, runScan]);

  const cancelAmbiguousLine = useCallback(() => {
    setAmbiguousScan(null);
    setAmbiguousChoice(undefined);
    focusScanner();
  }, [focusScanner]);

  // ---------- keyboard-wedge fallback ----------
  // The visible field is the capture path; this only redirects a scan that
  // started while focus sat elsewhere, so the first characters aren't lost.
  useEffect(() => {
    if (!scanActive || scanLocked) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (scanInFlightRef.current) return;

      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.isContentEditable)
      ) {
        return;
      }

      if (
        event.key.length !== 1 ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey
      ) {
        return;
      }

      event.preventDefault();
      const now = Date.now();
      const stale = now - lastKeyAtRef.current > SCAN_BUFFER_RESET_MS;
      lastKeyAtRef.current = now;
      focusScanner();
      setScanValue((previous) => (stale ? event.key : previous + event.key));
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [focusScanner, scanActive, scanLocked]);

  // Keep the field ready whenever scanning becomes possible again.
  useEffect(() => {
    if (scanActive && !scanLocked) focusScanner();
  }, [focusScanner, scanActive, scanLocked]);

  useEffect(() => () => window.clearTimeout(feedbackTimerRef.current), []);

  useEffect(() => {
    if (interactive) return;
    setManualLine(null);
    setManualReason(undefined);
    setWeightDraft(null);
    setAmbiguousScan(null);
    setAmbiguousChoice(undefined);
  }, [interactive]);

  // ---------- manual verify ----------
  const submitManual = async () => {
    if (!interactive || !manualLine || !manualReason) return;
    beginMutation();
    try {
      setManualSaving(true);
      const { data } = await EndPoints.order.manualCashCheck(
        orderId,
        {
          productOrderId: manualLine.productOrderId,
          reason: manualReason,
        },
        { suppressErrorNotification: true },
      );
      const mutation = data;
      applyMutation(mutation);
      if (mutation.line) playSuccess();
      setManualLine(null);
      setManualReason(undefined);
    } catch (error) {
      reportOperationError(error);
      setManualLine(null);
      setManualReason(undefined);
    } finally {
      setManualSaving(false);
      await finishMutation();
      focusScanner();
    }
  };

  // ---------- manual weight ----------
  const weightGrams = kilogramsToGrams(weightKilograms);
  const weightGramsValid = isWholeGramAmountInRange(
    weightGrams,
    CASH_CHECK_MIN_WEIGHT_GRAMS,
    CASH_CHECK_MAX_WEIGHT_GRAMS,
  );

  const submitManualWeight = async () => {
    if (!interactive || !weightDraft || !weightReason) return;
    if (weightGrams === undefined || !weightGramsValid) {
      errorNotification(t("cash_check_weight_out_of_range"));
      return;
    }

    // Weights leave the browser as whole grams; the kilogram field is display.
    const request = {
      productOrderId: weightDraft.line.productOrderId,
      measuredQuantityPhysical: weightGrams,
      reason: weightReason,
      operationToken: weightDraft.operationToken,
    };

    beginMutation();
    setWeightSaving(true);
    setWeightResult(null);
    try {
      let mutation: ICashCheckMutationResponse;
      try {
        const { data } = await EndPoints.order.manualWeightCashCheck(
          orderId,
          request,
          { suppressErrorNotification: true },
        );
        mutation = data;
      } catch (initialError: any) {
        if (!isAmbiguousRequestError(initialError)) throw initialError;
        const { data } = await EndPoints.order.manualWeightCashCheck(
          orderId,
          request,
        );
        mutation = data;
      }

      applyMutation(mutation);
      if (mutation.duplicate) {
        showFeedback({
          type: "warning",
          text: t("cash_check_duplicate_operation"),
        });
      } else if (mutation.line) {
        playSuccess();
        if (mutation.weightAdjustment)
          setWeightResult(mutation.weightAdjustment);
        showFeedback({
          type: "success",
          text: `${t("cash_check_weight_recorded")} — ${formatWeightFromGrams(
            request.measuredQuantityPhysical,
            weightUnitLabels,
          )}`,
        });
      }
      setWeightDraft(null);
      setWeightKilograms(null);
      setWeightReason(undefined);
    } catch (error) {
      reportOperationError(error);
      // The server refused, so nothing was recorded: the dialog stays open for
      // a correction, but the next attempt is a new operation with a new token.
      setWeightDraft((previous) =>
        previous ? { ...previous, operationToken: createUuidV4() } : previous,
      );
    } finally {
      setWeightSaving(false);
      await finishMutation();
      focusScanner();
    }
  };

  // ---------- undo ----------
  const undoVerification = async (
    verificationId: number,
    isFinalizationCycle = false,
  ) => {
    if (!interactive) return;
    beginMutation();
    try {
      setMutatingIds((previous) => [...previous, verificationId]);
      const { data } = await EndPoints.order.undoCashCheckVerification(
        orderId,
        verificationId,
        { suppressErrorNotification: true },
      );
      applyMutation(data);
      // Pending scale scans have no financial effect yet; manual-weight undo
      // keeps its existing behavior. The re-sync supplies the server truth.
      setWeightResult(null);
      if (isFinalizationCycle) {
        playSuccess();
        showFeedback({
          type: "success",
          text: t("cash_check_undo_weight_cycle_success"),
        });
      }
    } catch (error) {
      reportOperationError(error);
    } finally {
      setMutatingIds((previous) =>
        previous.filter((id) => id !== verificationId),
      );
      await finishMutation();
      focusScanner();
    }
  };

  // ---------- rendering ----------
  const renderLineStatus = (line: ICashCheckLine) => {
    if (line.isOverVerified) {
      return <Tag title={t("cash_check_over_verified")} color="orange" />;
    }
    if (line.isComplete) {
      if (isWeightVerificationMode(line.verificationMode)) {
        return (
          <Tag title={t("cash_check_weight_minimum_satisfied")} color="green" />
        );
      }
      return <Tag title={t("cash_check_line_complete")} color="green" />;
    }
    return (
      <AntdTag color="red">
        {t("cash_check_remaining")}:{" "}
        {Math.max(line.requiredVerifications - line.verifiedCount, 0)}
      </AntdTag>
    );
  };

  const renderVerifications = (line: ICashCheckLine) => {
    const timeline = analyzeWeightVerificationTimeline(line.verifications);
    const { latestPendingScaleScanId, latestManualWeightId } = timeline;

    return (
      <Space direction="vertical" size={0} style={{ width: "100%" }}>
        {line.verifications.length === 0 && (
          <Typography.Text type="secondary">
            {t("cash_check_no_verifications")}
          </Typography.Text>
        )}
        {timeline.entries.map((entry) => {
          if (entry.kind === "cycle") {
            const { finalization, scaleScans } = entry.cycle;
            const undoAllowed =
              canUndo && canAdjustWeight && entry.cycle.isUndoableByOrder;
            const showNewerEventHint =
              interactive &&
              canUndo &&
              canAdjustWeight &&
              entry.cycle.isLatestFinalization &&
              entry.cycle.hasNewerWeightEvent;

            return (
              <div
                key={finalization.id}
                className={`${styles.verificationRow} ${styles.finalizationCycle}`}
              >
                <Tag
                  title={t("cash_check_weight_finalization_cycle")}
                  color="purple"
                />
                <span className={styles.verificationMeta}>
                  {t("cash_check_previous_weight")}:{" "}
                  {formatWeightFromGrams(
                    finalization.previousQuantityPhysical,
                    weightUnitLabels,
                  )}
                  {" ← "}
                  {t("cash_check_measured_weight")}:{" "}
                  {formatWeightFromGrams(
                    finalization.measuredQuantityPhysical,
                    weightUnitLabels,
                  )}
                </span>
                <AntdTag color="blue">
                  {t("cash_check_weight_packages_count", {
                    count: scaleScans.length,
                  })}
                </AntdTag>
                {scaleScans.length === 1 && (
                  <span className={styles.savedPackageCode}>
                    {scaleScans[0].scannedBarcode && (
                      <Typography.Text code>
                        {scaleScans[0].scannedBarcode}
                      </Typography.Text>
                    )}
                    <Typography.Text type="secondary">
                      {formatWeightFromGrams(
                        scaleScans[0].measuredQuantityPhysical,
                        weightUnitLabels,
                      )}
                    </Typography.Text>
                  </span>
                )}
                {scaleScans.length > 1 && (
                  <details className={styles.savedPackages}>
                    <summary>
                      {t("cash_check_weight_cycle_show_labels")}
                    </summary>
                    <div className={styles.savedPackageCodes}>
                      {scaleScans.map((scaleScan) => (
                        <span
                          className={styles.savedPackageCode}
                          key={scaleScan.id}
                        >
                          {scaleScan.scannedBarcode && (
                            <Typography.Text code>
                              {scaleScan.scannedBarcode}
                            </Typography.Text>
                          )}
                          <Typography.Text type="secondary">
                            {formatWeightFromGrams(
                              scaleScan.measuredQuantityPhysical,
                              weightUnitLabels,
                            )}
                          </Typography.Text>
                        </span>
                      ))}
                    </div>
                  </details>
                )}
                <span className={styles.verificationMeta}>
                  {formatPersonName(finalization.verifiedBy)} —{" "}
                  {finalization.verifiedAt
                    ? moment(finalization.verifiedAt).format(
                        "YYYY-MM-DD HH:mm:ss",
                      )
                    : "-"}
                </span>
                {interactive && undoAllowed && (
                  <Popconfirm
                    title={t("cash_check_undo_weight_cycle_confirm")}
                    okText={t("confirm")}
                    cancelText={t("cancel")}
                    onConfirm={() => undoVerification(finalization.id, true)}
                  >
                    <Button
                      danger
                      size="small"
                      type="text"
                      icon={<UndoOutlined />}
                      loading={mutatingIds.includes(finalization.id)}
                      disabled={mutatingIds.includes(finalization.id)}
                    >
                      {t("cash_check_undo_weight_cycle")}
                    </Button>
                  </Popconfirm>
                )}
                {showNewerEventHint && (
                  <Typography.Text type="warning" className={styles.weightMeta}>
                    {t("cash_check_weight_cycle_newer_event_hint")}
                  </Typography.Text>
                )}
              </div>
            );
          }

          const { verification } = entry;
          const isWeight = isWeightVerificationMethod(verification.method);
          const isPendingScaleScan = isPendingAutomaticScaleScan(verification);
          const isFinalizedScaleScan =
            verification.method === CashCheckVerificationMethod.SCALE_SCAN &&
            verification.finalizationVerificationId != null;
          const undoAllowed =
            verification.method === CashCheckVerificationMethod.SCALE_SCAN
              ? canUndo &&
                canAdjustWeight &&
                isPendingScaleScan &&
                verification.id === latestPendingScaleScanId
              : verification.method ===
                  CashCheckVerificationMethod.MANUAL_WEIGHT
                ? canUndo &&
                  canAdjustWeight &&
                  verification.id === latestManualWeightId
                : canUndo;

          return (
            <div key={verification.id} className={styles.verificationRow}>
              <Tag
                title={t(
                  getCashCheckVerificationMethodName(verification.method),
                )}
                color={getCashCheckVerificationMethodColor(verification.method)}
              />
              {isPendingScaleScan && (
                <AntdTag color="gold">
                  {t("cash_check_weight_scan_pending")}
                </AntdTag>
              )}
              {isFinalizedScaleScan && (
                <AntdTag color="default">
                  {t("cash_check_weight_scan_finalized")}
                </AntdTag>
              )}
              {verification.isSystemFinalization && (
                <AntdTag color="purple">
                  {t("cash_check_system_finalization")}
                </AntdTag>
              )}
              {(verification.method === CashCheckVerificationMethod.MANUAL ||
                verification.method ===
                  CashCheckVerificationMethod.MANUAL_WEIGHT) &&
                verification.manualReason && (
                  <Tag
                    title={t(
                      getCashCheckManualReasonName(verification.manualReason),
                    )}
                    color="volcano"
                  />
                )}
              {verification.scannedBarcode && (
                <Typography.Text code>
                  {verification.scannedBarcode}
                </Typography.Text>
              )}
              {isWeight && (
                <span className={styles.verificationMeta}>
                  {t("cash_check_measured_weight")}:{" "}
                  {formatWeightFromGrams(
                    verification.measuredQuantityPhysical,
                    weightUnitLabels,
                  )}
                  {verification.previousQuantityPhysical !== null &&
                    verification.previousQuantityPhysical !== undefined && (
                      <>
                        {" · "}
                        {t("cash_check_previous_weight")}:{" "}
                        {formatWeightFromGrams(
                          verification.previousQuantityPhysical,
                          weightUnitLabels,
                        )}
                      </>
                    )}
                  {Boolean(verification.shortageCreatedQuantityPhysical) && (
                    <>
                      {" · "}
                      <Typography.Text type="warning">
                        {t("cash_check_shortage_created")}:{" "}
                        {formatWeightFromGrams(
                          verification.shortageCreatedQuantityPhysical,
                          weightUnitLabels,
                        )}
                      </Typography.Text>
                    </>
                  )}
                </span>
              )}
              {isWeight && Boolean(verification.packageCount) && (
                <AntdTag color="blue">
                  {t("cash_check_weight_packages_count", {
                    count: verification.packageCount,
                  })}
                </AntdTag>
              )}
              {isWeight && Boolean(verification.scalePackages?.length) && (
                <details className={styles.savedPackages}>
                  <summary>
                    {t("cash_check_weight_packages_show_saved")}
                  </summary>
                  <div className={styles.savedPackageCodes}>
                    {verification.scalePackages?.map((savedPackage, index) => {
                      const normalized =
                        normalizeSavedScalePackage(savedPackage);
                      const { barcode, measuredQuantityPhysical } = normalized;
                      const key = normalized.itemToken ?? `${barcode}-${index}`;

                      return (
                        <span className={styles.savedPackageCode} key={key}>
                          <Typography.Text code>{barcode}</Typography.Text>
                          {measuredQuantityPhysical !== null &&
                            measuredQuantityPhysical !== undefined && (
                              <Typography.Text type="secondary">
                                {formatWeightFromGrams(
                                  measuredQuantityPhysical,
                                  weightUnitLabels,
                                )}
                              </Typography.Text>
                            )}
                        </span>
                      );
                    })}
                  </div>
                </details>
              )}
              <span className={styles.verificationMeta}>
                {formatPersonName(verification.verifiedBy)} —{" "}
                {verification.verifiedAt
                  ? moment(verification.verifiedAt).format(
                      "YYYY-MM-DD HH:mm:ss",
                    )
                  : "-"}
              </span>
              {interactive && undoAllowed && (
                <Popconfirm
                  title={
                    isPendingScaleScan
                      ? t("cash_check_undo_scale_scan_confirm")
                      : isWeight
                        ? t("cash_check_undo_weight_confirm")
                        : t("cash_check_undo_confirm")
                  }
                  okText={t("confirm")}
                  cancelText={t("cancel")}
                  onConfirm={() => undoVerification(verification.id)}
                >
                  <Button
                    danger
                    size="small"
                    type="text"
                    icon={<UndoOutlined />}
                    loading={mutatingIds.includes(verification.id)}
                  >
                    {t("cash_check_undo")}
                  </Button>
                </Popconfirm>
              )}
            </div>
          );
        })}
      </Space>
    );
  };

  const columns: ColumnsType<ICashCheckLine> = [
    {
      title: t("image"),
      key: "image",
      width: 70,
      render: (_, line) => {
        const imageUrl = getActualProduct(line)?.image?.url;
        return imageUrl ? (
          <Image
            src={imageUrl}
            width={48}
            height={48}
            style={{ objectFit: "contain" }}
            preview={{ mask: null }}
          />
        ) : (
          <BarcodeOutlined style={{ fontSize: 24, opacity: 0.35 }} />
        );
      },
    },
    {
      title: t("product"),
      key: "product",
      render: (_, line) => {
        const joined = getJoined(line);
        return (
          <div className={styles.productCell}>
            <Typography.Text strong>{getProductName(line)}</Typography.Text>
            <div className={styles.productBadges}>
              {joined?.isAlternative && (
                <Tag title={t("cash_check_alternative")} color="purple" />
              )}
              {line.verificationMode ? (
                <Tooltip
                  title={
                    isWeightVerificationMode(line.verificationMode)
                      ? t("cash_check_mode_weight_hint")
                      : undefined
                  }
                >
                  <span>
                    <Tag
                      title={t(
                        getCashCheckVerificationModeName(line.verificationMode),
                      )}
                      color={getCashCheckVerificationModeColor(
                        line.verificationMode,
                      )}
                    />
                  </span>
                </Tooltip>
              ) : (
                getActualProduct(line)?.soldByUnit && (
                  <Tooltip title={t("cash_check_weighted_hint")}>
                    <span>
                      <Tag title={t("cash_check_weighted")} color="blue" />
                    </span>
                  </Tooltip>
                )
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: t("cash_check_reserved_quantity"),
      key: "reservedQuantity",
      width: 110,
      align: "center",
      render: (_, line) => getReservedQuantityDisplay(line),
    },
    {
      title: t("cash_check_weight"),
      key: "weight",
      width: 190,
      align: "center",
      render: (_, line) => {
        if (!isWeightVerificationMode(line.verificationMode)) return "-";
        const measuredWeight = toFiniteNumber(line.measuredQuantityPhysical);
        const appliedWeight = toFiniteNumber(line.actualQuantityPhysical);
        const differenceFromApplied =
          toFiniteNumber(line.pendingDeltaQuantityPhysical) ??
          (measuredWeight !== undefined && appliedWeight !== undefined
            ? measuredWeight - appliedWeight
            : undefined);
        const isSecondWeightCycle =
          line.hasPendingWeightFinalization === true &&
          line.verifications.some(
            (verification) =>
              verification.method ===
                CashCheckVerificationMethod.WEIGHT_FINALIZATION ||
              verification.finalizationVerificationId !== null,
          );

        return (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>
              {t("cash_check_scanned_weight")}:{" "}
              {formatWeightFromGrams(
                line.measuredQuantityPhysical,
                weightUnitLabels,
              )}
            </Typography.Text>
            <Typography.Text type="secondary" className={styles.weightMeta}>
              {t("cash_check_applied_weight")}:{" "}
              {formatWeightFromGrams(
                line.actualQuantityPhysical,
                weightUnitLabels,
              )}
            </Typography.Text>
            <Typography.Text type="secondary" className={styles.weightMeta}>
              {t("cash_check_requested_weight")}:{" "}
              {formatWeightFromGrams(
                line.requestedQuantityPhysical ??
                  line.expectedQuantityPhysical ??
                  line.allocatedQuantityPhysical,
                weightUnitLabels,
              )}
            </Typography.Text>
            {line.weightDifferencePhysical !== null &&
              line.weightDifferencePhysical !== undefined && (
                <Typography.Text
                  type={
                    line.weightDifferencePhysical === 0 ? "success" : "warning"
                  }
                  className={styles.weightMeta}
                >
                  {t("cash_check_weight_delta_from_expected")}:{" "}
                  {formatSignedWeightFromGrams(
                    line.weightDifferencePhysical,
                    weightUnitLabels,
                  )}
                </Typography.Text>
              )}
            {isSecondWeightCycle && differenceFromApplied !== undefined && (
              <Typography.Text
                type={differenceFromApplied === 0 ? "success" : "warning"}
                className={styles.weightMeta}
              >
                {t("cash_check_weight_delta_from_applied")}:{" "}
                {formatSignedWeightFromGrams(
                  differenceFromApplied,
                  weightUnitLabels,
                )}
              </Typography.Text>
            )}
            <Space size={4} wrap>
              {Boolean(line.packageCount) && (
                <AntdTag color="blue">
                  {t("cash_check_weight_packages_count", {
                    count: line.packageCount,
                  })}
                </AntdTag>
              )}
              {line.hasPendingWeightFinalization && (
                <AntdTag color="gold">
                  {t("cash_check_pending_weight_finalization")}
                </AntdTag>
              )}
            </Space>
            {Boolean(line.shortageQuantityPhysical) && (
              <Typography.Text type="warning" className={styles.weightMeta}>
                {t("cash_check_shortage")}:{" "}
                {formatWeightFromGrams(
                  line.shortageQuantityPhysical,
                  weightUnitLabels,
                )}
              </Typography.Text>
            )}
          </Space>
        );
      },
    },
    {
      title: t("cash_check_required"),
      dataIndex: "requiredVerifications",
      key: "requiredVerifications",
      width: 110,
      align: "center",
    },
    {
      title: t("cash_check_verified"),
      key: "verifiedCount",
      width: 120,
      align: "center",
      render: (_, line) => (
        <Space size={8}>
          <Typography.Text
            strong
            type={
              line.isOverVerified
                ? "warning"
                : line.isComplete
                  ? "success"
                  : undefined
            }
          >
            {line.verifiedCount}
          </Typography.Text>
          {pendingLineId === line.productOrderId && (
            <LoadingOutlined style={{ fontSize: 16 }} />
          )}
        </Space>
      ),
    },
    {
      title: t("status"),
      key: "status",
      width: 130,
      align: "center",
      render: (_, line) => renderLineStatus(line),
    },
  ];

  if (interactive && canManual) {
    columns.push({
      title: t("actions"),
      key: "actions",
      width: 160,
      align: "center",
      render: (_, line) => {
        if (isWeightVerificationMode(line.verificationMode)) {
          if (line.isComplete) return null;
          if (!canAdjustWeight) return null;
          return (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openWeightModal(line)}
            >
              {t("cash_check_manual_weight")}
            </Button>
          );
        }

        return (
          canManual &&
          !line.isComplete && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => setManualLine(line)}
            >
              {t("cash_check_manual_verify")}
            </Button>
          )
        );
      },
    });
  }

  const renderExpandedLine = (line: ICashCheckLine) => (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      {renderVerifications(line)}
    </Space>
  );

  // ---------- visibility ----------
  if (loading && !check) {
    return isCashStage ? (
      <Card className={styles.panelCard}>
        <Spin />
      </Card>
    ) : null;
  }
  if (!check) return null;
  if (!check.enabled && !hasActiveVerifications) return null;
  if (!isCashStage && !hasActiveVerifications) return null;

  const calculatedPercent =
    totals.required > 0
      ? Math.round((totals.verified / totals.required) * 100)
      : 0;
  const percent = check.isComplete ? 100 : Math.min(calculatedPercent, 99);

  const body = (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      {!check.enabled && (
        <Alert
          type="info"
          showIcon
          message={t("cash_check_read_only_disabled")}
        />
      )}
      {scanActive && (
        <Input
          ref={scannerInputRef}
          className={styles.scannerInput}
          size="large"
          allowClear
          disabled={scanLocked}
          prefix={<BarcodeOutlined />}
          placeholder={t("cash_check_scanner_placeholder")}
          value={scanValue}
          onChange={(event) => setScanValue(event.target.value)}
          onPressEnter={() => {
            // The raw string goes out untouched — no parsing, no trimming of
            // leading zeros, product and scale labels alike.
            const barcode = scanValue.trim();
            if (barcode.length >= MIN_BARCODE_LENGTH) submitScan(barcode);
          }}
        />
      )}
      {feedback && (
        <Alert
          type={feedback.type}
          showIcon
          message={feedback.text}
          description={
            feedback.parsedProductCode ? (
              <>
                <div>
                  {t("cash_check_parsed_product_code")}:{" "}
                  <span className={styles.feedbackDetail}>
                    {feedback.parsedProductCode}
                  </span>
                </div>
                {weightedLineBarcodes.length > 0 && (
                  <div>
                    {t("cash_check_registered_barcodes")}:{" "}
                    <span className={styles.feedbackDetail}>
                      {weightedLineBarcodes.join(", ")}
                    </span>
                  </div>
                )}
              </>
            ) : undefined
          }
          closable
          onClose={() => setFeedback(null)}
        />
      )}
      {weightResult && (
        <Alert
          type="success"
          showIcon
          closable
          onClose={() => setWeightResult(null)}
          message={`${t(
            weightResult.pendingFinalization
              ? "cash_check_weight_label_recorded"
              : "cash_check_weight_recorded",
          )}: ${formatWeightFromGrams(
            weightResult.packageMeasuredQuantityPhysical ??
              weightResult.measuredQuantityPhysical,
            weightUnitLabels,
          )}`}
          description={
            <Space direction="vertical" size={0}>
              {weightResult.pendingFinalization && (
                <>
                  <span>
                    {t("cash_check_cumulative_scanned_weight")}:{" "}
                    {formatWeightFromGrams(
                      weightResult.measuredQuantityPhysical,
                      weightUnitLabels,
                    )}
                  </span>
                  <span>
                    {t("cash_check_weight_packages_count", {
                      count: weightResult.packageCount ?? 0,
                    })}
                  </span>
                  <Typography.Text type="warning">
                    {t("cash_check_pending_weight_finalization")}
                  </Typography.Text>
                </>
              )}
              {(weightResult.pendingDeltaQuantityPhysical ??
                weightResult.deltaQuantityPhysical) !== null &&
                (weightResult.pendingDeltaQuantityPhysical ??
                  weightResult.deltaQuantityPhysical) !== undefined && (
                  <span>
                    {t("cash_check_weight_delta_from_applied")}:{" "}
                    {formatSignedWeightFromGrams(
                      weightResult.pendingDeltaQuantityPhysical ??
                        weightResult.deltaQuantityPhysical,
                      weightUnitLabels,
                    )}
                  </span>
                )}
              {!weightResult.pendingFinalization && (
                <>
                  <span>
                    {t("cash_check_inventory_covered")}:{" "}
                    {formatWeightFromGrams(
                      weightResult.inventoryCoveredQuantityPhysical,
                      weightUnitLabels,
                    )}
                  </span>
                  {Boolean(weightResult.shortageCreatedQuantityPhysical) && (
                    <span>
                      {t("cash_check_shortage_created")}:{" "}
                      {formatWeightFromGrams(
                        weightResult.shortageCreatedQuantityPhysical,
                        weightUnitLabels,
                      )}
                    </span>
                  )}
                </>
              )}
            </Space>
          }
        />
      )}
      {check.shortageSummary?.hasOpenShortage && (
        <Alert
          type="warning"
          showIcon
          message={t("cash_check_open_shortage_warning")}
          description={`${t("cash_check_open_shortage_count")}: ${
            check.shortageSummary.openCount
          } — ${t("cash_check_open_shortage_quantity")}: ${formatWeightFromGrams(
            check.shortageSummary.openQuantityPhysical,
            weightUnitLabels,
          )}`}
        />
      )}

      <div className={styles.progressRow}>
        <Progress
          className={styles.progressBar}
          percent={percent}
          status={
            check.isComplete ? "success" : interactive ? "active" : "normal"
          }
          format={() => `${totals.verified} / ${totals.required}`}
        />
        {check.isComplete && (
          <Tag title={t("cash_check_complete")} color="green" />
        )}
        {(hasUnattributedPending || (isBusy && !pendingScan)) && (
          <span className={styles.pendingChip}>
            <LoadingOutlined />
            {t("cash_check_confirming")}
          </span>
        )}
      </div>

      <Table
        rowKey="productOrderId"
        size="small"
        columns={columns}
        dataSource={check.lines}
        pagination={false}
        scroll={{ x: true }}
        expandable={{
          expandedRowRender: renderExpandedLine,
          rowExpandable: () => true,
          expandedRowKeys: expandedLineIds,
          onExpandedRowsChange: (keys) => setExpandedLineIds([...keys]),
        }}
      />
    </Space>
  );

  const header = (
    <div className={styles.headerRow}>
      <span className={styles.headerTitle}>
        <BarcodeOutlined />
        {t("cash_check_title")}
        {!check.enabled && <AntdTag>{t("cash_check_read_only")}</AntdTag>}
        {check.isComplete && (
          <CheckCircleOutlined style={{ color: "#52c41a" }} />
        )}
      </span>
      <Space size="small" wrap onClick={(event) => event.stopPropagation()}>
        {scanActive && (
          <span className={styles.scannerChip}>
            <span className={styles.scannerChipDot} />
            {t("cash_check_scanner_ready")}
          </span>
        )}
        {isCashStage && canFinishCash && (
          <Tooltip
            title={
              isBusy
                ? t("cash_check_confirming")
                : finishBlocked
                  ? t("cash_check_finish_blocked")
                  : undefined
            }
          >
            <span>
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                disabled={finishBlocked}
                loading={finishing || isBusy}
                onClick={onFinishCash}
              >
                {t("finish_accounting_finalization")}
              </Button>
            </span>
          </Tooltip>
        )}
      </Space>
    </div>
  );

  return (
    <>
      {isCashStage ? (
        <Card className={styles.panelCard} title={header}>
          {body}
        </Card>
      ) : (
        <Collapse>
          <Panel header={header} key={1}>
            {body}
          </Panel>
        </Collapse>
      )}

      <Modal
        title={t("cash_check_ambiguous_line_title")}
        open={Boolean(ambiguousScan)}
        onCancel={cancelAmbiguousLine}
        onOk={confirmAmbiguousLine}
        okText={t("confirm")}
        cancelText={t("cancel")}
        okButtonProps={{ disabled: ambiguousChoice === undefined }}
        destroyOnClose
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Alert
            type="info"
            showIcon
            message={t("cash_check_ambiguous_line_hint")}
          />
          <Typography.Text code>{ambiguousScan?.barcode}</Typography.Text>
          <Radio.Group
            value={ambiguousChoice}
            onChange={(event) => setAmbiguousChoice(event.target.value)}
          >
            <Space direction="vertical">
              {(ambiguousScan?.candidateProductOrderIds ?? []).map(
                (productOrderId) => {
                  const line = findLine(productOrderId);
                  const joined = productOrderById.get(productOrderId);
                  return (
                    <Radio key={productOrderId} value={productOrderId}>
                      <Space size={8} wrap>
                        <Typography.Text strong>
                          {line
                            ? getProductName(line)
                            : joined?.product?.name || `#${productOrderId}`}
                        </Typography.Text>
                        {joined && (
                          <Typography.Text type="secondary">
                            {t("cash_check_reserved_quantity")}:{" "}
                            {formatRecordReservedQuantityWithUnit(joined)}
                          </Typography.Text>
                        )}
                        {line?.isComplete &&
                          isWeightVerificationMode(line.verificationMode) && (
                            <Tag
                              title={t("cash_check_weight_minimum_satisfied")}
                              color="green"
                            />
                          )}
                      </Space>
                    </Radio>
                  );
                },
              )}
            </Space>
          </Radio.Group>
        </Space>
      </Modal>

      <Modal
        title={t("cash_check_manual_weight")}
        open={Boolean(weightDraft)}
        onCancel={() => {
          setWeightDraft(null);
          setWeightKilograms(null);
          setWeightReason(undefined);
          focusScanner();
        }}
        onOk={submitManualWeight}
        okText={t("confirm")}
        cancelText={t("cancel")}
        confirmLoading={weightSaving}
        okButtonProps={{ disabled: !weightReason || !weightGramsValid }}
        destroyOnClose
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Typography.Text>
            {weightDraft ? getProductName(weightDraft.line) : ""}
          </Typography.Text>
          <div>
            <Typography.Text strong>
              {t("cash_check_measured_weight")} ({t("cash_check_unit_kilogram")}
              )
            </Typography.Text>
            <InputNumber
              style={{ width: "100%", marginTop: 8 }}
              min={CASH_CHECK_MIN_WEIGHT_GRAMS / 1000}
              max={CASH_CHECK_MAX_WEIGHT_GRAMS / 1000}
              step={0.001}
              precision={3}
              value={weightKilograms}
              onChange={(value) => setWeightKilograms(value)}
              placeholder="0.000"
            />
            {/* The value actually sent, so the operator sees the rounding. */}
            <Typography.Text type="secondary">
              {weightGrams !== undefined && weightGramsValid
                ? `= ${weightGrams.toLocaleString()} ${t("cash_check_unit_gram")}`
                : t("cash_check_weight_out_of_range")}
            </Typography.Text>
          </div>
          <div>
            <Typography.Text strong>
              {t("cash_check_manual_reason")}
            </Typography.Text>
            <Select
              style={{ width: "100%", marginTop: 8 }}
              placeholder={t("cash_check_manual_reason")}
              value={weightReason}
              onChange={(value) => setWeightReason(value)}
              options={[
                {
                  label: t(
                    getCashCheckManualReasonName(
                      CashCheckManualReason.EMERGENCY,
                    ),
                  ),
                  value: CashCheckManualReason.EMERGENCY,
                },
                {
                  label: t(
                    getCashCheckManualReasonName(
                      CashCheckManualReason.NO_BARCODE,
                    ),
                  ),
                  value: CashCheckManualReason.NO_BARCODE,
                },
              ]}
            />
          </div>
        </Space>
      </Modal>

      <Modal
        title={t("cash_check_manual_verify")}
        open={Boolean(manualLine)}
        onCancel={() => {
          setManualLine(null);
          setManualReason(undefined);
        }}
        onOk={submitManual}
        okText={t("confirm")}
        cancelText={t("cancel")}
        confirmLoading={manualSaving}
        okButtonProps={{ disabled: !manualReason }}
        destroyOnClose
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Typography.Text>
            {manualLine ? getProductName(manualLine) : ""}
          </Typography.Text>
          <div>
            <Typography.Text strong>
              {t("cash_check_manual_reason")}
            </Typography.Text>
            <Select
              style={{ width: "100%", marginTop: 8 }}
              placeholder={t("cash_check_manual_reason")}
              value={manualReason}
              onChange={(value) => setManualReason(value)}
              options={[
                {
                  label: t(
                    getCashCheckManualReasonName(
                      CashCheckManualReason.EMERGENCY,
                    ),
                  ),
                  value: CashCheckManualReason.EMERGENCY,
                },
                {
                  label: t(
                    getCashCheckManualReasonName(
                      CashCheckManualReason.NO_BARCODE,
                    ),
                  ),
                  value: CashCheckManualReason.NO_BARCODE,
                },
              ]}
            />
          </div>
        </Space>
      </Modal>
    </>
  );
};

export default OrderCashCheckPanel;
